import { useCallback } from 'react'
import { submitEnrollmentStepRequest } from '@/api/enrollment-api'
import { ENROLLMENT_COMMON_MESSAGES } from '@/features/enrollment/constants/enrollmentContent'
import type {
  DocusignRefs,
  DocusignSetters,
  DocusignState,
  FlowNavigation,
  FlowSessionState,
  HydrateEnrollmentSessionFn
} from '../types'
import { getSelfEnrollmentSubmissionStep, SELF_ENROLLMENT_STEPS } from '../utils/steps'
import {
  useDocusignFinalizationPolling,
  useDocusignReturnListener,
  useDocusignWindowFocusEffects
} from './docusign/effects'
import { useDocusignModalControls } from './docusign/useDocusignModalControls'
import { useDocusignSignatureFlow } from './docusign/useDocusignSignatureFlow'
import { getRequestErrorMessage } from '../utils/utils'

type UseDocusignArgs = {
  currentStep: number
  sessionState: Pick<FlowSessionState, 'enrollmentSessionToken' | 'setSignatureCompleted'>
  docusignState: DocusignState
  hydration: {
    hydrateEnrollmentSession: HydrateEnrollmentSessionFn
  }
  navigation: Pick<FlowNavigation, 'navigate' | 'goToStep'>
  actions: {
    finalizeEnrollment: () => Promise<void>
  }
  setters: DocusignSetters
  refs: DocusignRefs
}

export function useDocusign({
  currentStep,
  sessionState,
  docusignState,
  hydration,
  navigation,
  actions,
  setters,
  refs
}: UseDocusignArgs) {
  const { enrollmentSessionToken, setSignatureCompleted } = sessionState
  const { isDocusignModalOpen, isSignatureSigned, isFinalizingDocusign } = docusignState
  const { hydrateEnrollmentSession } = hydration
  const { navigate, goToStep } = navigation
  const { finalizeEnrollment } = actions
  const {
    setStepSubmissionError,
    setDocusignUrl,
    setDocusignFrameKey,
    setIsDocusignIframeLoading,
    setIsFinalizingDocusign,
    setIsDocusignModalOpen,
    setSignatureStatus,
    setIsSignatureSigned,
    setIsPreparingDocusign,
    setIsCheckingSignature
  } = setters
  const {
    hasLoadedDocusignIframeRef,
    hasBlurredDuringDocusignRef,
    isRestartingDocusignRef,
    isPreparingDocusignRef
  } = refs

  const syncSignedStep = useCallback(async () => {
    if (!enrollmentSessionToken) {
      return
    }

    try {
      await submitEnrollmentStepRequest({
        sessionToken: enrollmentSessionToken,
        data: {
          step: getSelfEnrollmentSubmissionStep(SELF_ENROLLMENT_STEPS.SIGN_DOCUMENTS)
        }
      })

      await hydrateEnrollmentSession(false)
    } catch (error) {
      setStepSubmissionError(
        getRequestErrorMessage(error, ENROLLMENT_COMMON_MESSAGES.saveStepFailed)
      )
      throw error
    }
  }, [enrollmentSessionToken, hydrateEnrollmentSession, setStepSubmissionError])

  const {
    closeDocusignModal,
    openDocusignModalWithUrl,
    completeSigningFlow,
    handleContinueFromDocusign,
    resetDocusignSessionState,
    setPreparingDocusignState,
    handleDocusignIframeLoad
  } = useDocusignModalControls({
    refs: {
      hasLoadedDocusignIframeRef,
      hasBlurredDuringDocusignRef
    },
    setters: {
      setDocusignUrl,
      setDocusignFrameKey,
      setIsDocusignIframeLoading,
      setIsFinalizingDocusign,
      setIsDocusignModalOpen,
      setIsCheckingSignature,
      setIsSignatureSigned
    },
    setSignatureCompleted,
    goToStep,
    finalizeEnrollment,
    syncSignedStep
  })

  const { handleOpenDocusignModal, refreshSignatureStatus } = useDocusignSignatureFlow({
    enrollmentSessionToken,
    hydrateEnrollmentSession,
    navigate,
    goToStep,
    setStepSubmissionError,
    setSignatureStatus,
    setIsPreparingDocusign,
    setIsCheckingSignature,
    setIsFinalizingDocusign,
    setIsDocusignIframeLoading,
    isPreparingDocusignRef,
    isRestartingDocusignRef,
    resetDocusignSessionState,
    setPreparingDocusignState,
    completeSigningFlow,
    openDocusignModalWithUrl
  })

  useDocusignFinalizationPolling({
    currentStep,
    enrollmentSessionToken,
    isDocusignModalOpen,
    isFinalizingDocusign,
    isSignatureSigned,
    refreshSignatureStatus,
    setIsCheckingSignature
  })

  useDocusignWindowFocusEffects({
    isDocusignModalOpen,
    isSignatureSigned,
    hasLoadedDocusignIframeRef,
    hasBlurredDuringDocusignRef,
    setIsFinalizingDocusign,
    setIsDocusignIframeLoading,
    refreshSignatureStatus
  })

  useDocusignReturnListener({
    isDocusignModalOpen,
    isSignatureSigned,
    closeDocusignModal,
    refreshSignatureStatus,
    setIsFinalizingDocusign,
    setIsDocusignIframeLoading,
    setStepSubmissionError
  })

  return {
    handleOpenDocusignModal,
    handleCloseDocusignModal: closeDocusignModal,
    handleContinueFromDocusign,
    handleDocusignIframeLoad
  }
}
